import { useMemo, useState } from "react";
import { Alert, Button, RadioGroup } from "../../elements";
import { Text } from "../../elements/text/text";
import type { AddressData } from "../delivery-payment-info/delivery-payment-info";
import { Modal } from "../modal/modal";

export type AddressSelectorData = {
  addresses?: AddressData[];
  microcopies: {
    changeAddressButtonLabel: string;
    warningTitle: string;
    warningDescription: string;
    dialogTitle: string;
    dialogSubtitle: string;
    dialogSubmitButtonLabel: string;
    dialogErrorMessage: string;
  };
  onSelectAddress?: (address: AddressData) => Promise<{ ok: boolean }>;
  shouldShowCTA?: boolean;
};

const AddressLabel = (address: AddressData) => (
  <div className="flex flex-col">
    <Text variant="body-md">{address.companyName}</Text>
    <Text variant="body-md">{address.street}</Text>
    <Text variant="body-md">
      {address.postalCode} {address.city}
    </Text>
    <Text variant="body-md">{address.country}</Text>
  </div>
);

function findAddressById(
  addresses: AddressData[] | undefined,
  id: string | undefined
): AddressData | undefined {
  if (!(addresses && id)) {
    return;
  }
  return addresses.find((addr) => addr.id === id);
}

export function AddressSelector(props: Readonly<AddressSelectorData>) {
  const {
    addresses,
    microcopies,
    onSelectAddress,
    shouldShowCTA = true,
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  // Select Default address from the array otherwise use first item of the array
  // Addresses can be undefined -> Empty status -> Warning Alert displayed
  const defaultAddress =
    addresses?.find((address) => address.isDefault) ?? addresses?.[0];

  // Map addresses to create list of options for RadioGroup used in the modal
  const addressOptions = useMemo(
    () =>
      addresses?.map((address) => ({
        value: address.id,
        label: <AddressLabel {...address} />,
      })),
    [addresses]
  );

  // Confirmed address after successful API call - drives sorting and display
  const [confirmedAddress, setConfirmedAddress] = useState<string | undefined>(
    addressOptions?.find((opt) => opt.value === defaultAddress?.id)?.value
  );

  // Pending selection in modal before API confirmation (RadioGroup value)
  const [pendingAddress, setPendingAddress] = useState<string | undefined>(
    addressOptions?.find((opt) => opt.value === defaultAddress?.id)?.value
  );

  // Sort addresses in modal to show confirmed address on top, if it exists
  // Valued if addresses is an array, otherwise undefined cause no modal will be displayed
  const sortedAddressOptions = useMemo(() => {
    if (!addressOptions) {
      return;
    }
    if (confirmedAddress) {
      return [
        ...addressOptions.filter((opt) => opt.value === confirmedAddress),
        ...addressOptions.filter((opt) => opt.value !== confirmedAddress),
      ];
    }
    return addressOptions;
  }, [addressOptions, confirmedAddress]);

  // Open modal action - resets pending selection and error state to ensure clean state on each open
  const openModal = () => {
    setPendingAddress(confirmedAddress);
    setErrorMessage(undefined);
    setIsModalOpen(true);
  };

  // Close modal action - resets pending selection and error state to ensure clean state on next open
  const closeModal = () => {
    setPendingAddress(confirmedAddress);
    setErrorMessage(undefined);
    setIsModalOpen(false);
  };

  // Handle address selection confirmation - calls API and updates state based on result
  const handleSelectAddress = async (address: AddressData) => {
    // Skip API call if selected address is already the confirmed one
    if (address.id === confirmedAddress) {
      setIsModalOpen(false);
      return;
    }

    if (onSelectAddress) {
      setIsSubmitting(true);
      setErrorMessage(undefined);
      try {
        const result = await onSelectAddress(address);

        if (result.ok) {
          setConfirmedAddress(address.id);
          setIsModalOpen(false);
        } else {
          setErrorMessage(microcopies.dialogErrorMessage);
        }
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : microcopies.dialogErrorMessage
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!addresses || addresses.length === 0) {
    return (
      <Alert
        microcopies={{
          title: microcopies.warningTitle,
          description: microcopies.warningDescription,
        }}
        variant="warning"
      />
    );
  }

  const displayedAddress =
    findAddressById(addresses, confirmedAddress) ?? addresses[0];

  const handleSubmit = () => {
    const selectedAddress = findAddressById(addresses, pendingAddress);
    if (selectedAddress) {
      void handleSelectAddress(selectedAddress);
    }
  };

  return (
    <>
      <AddressLabel {...displayedAddress} />
      {addresses.length > 1 && shouldShowCTA ? (
        <Button
          className="mt-2 w-max font-semibold"
          onClick={openModal}
          size="default"
          variant="secondary"
        >
          {microcopies.changeAddressButtonLabel}
        </Button>
      ) : null}
      <Modal
        generalErrorMessage={errorMessage}
        headerCentered={false}
        isLoading={isSubmitting}
        isOpen={isModalOpen}
        modalClassName="max-w-md p-6"
        onClose={closeModal}
        onPrimaryAction={handleSubmit}
        primaryButtonText={microcopies.dialogSubmitButtonLabel}
        subtitle={microcopies.dialogSubtitle}
        title={microcopies.dialogTitle}
      >
        <div className="py-4">
          {sortedAddressOptions && (
            <RadioGroup
              name="shipping-address"
              onValueChange={setPendingAddress}
              options={sortedAddressOptions}
              value={pendingAddress}
            />
          )}
        </div>
      </Modal>
    </>
  );
}
